import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Chip,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import api, { API_BASE_URL } from "../../utils/api";
import MemberViolationDetails from "./MemberViolationDetails";

const ITEMS_PER_PAGE = 5;

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const nestedCardSx = {
  p: 2,
  borderRadius: "14px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(255,255,255,0.98)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  height: "100%",
};

const getStatusChipSx = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["resolved", "closed", "violation cured", "withdrawn / canceled"].includes(normalized)) {
    return { color: "#15803d", backgroundColor: "rgba(34,197,94,0.12)", fontWeight: 700 };
  }
  return { color: "#b45309", backgroundColor: "rgba(245,158,11,0.12)", fontWeight: 700 };
};

// ─── Toolbar ────────────────────────────────────────────────────────────────

const FilterToolbar = ({ search, onSearch, sortOrder, onSort, statuses, statusFilter, onStatusFilter }) => (
  <Box sx={{ mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexWrap: "wrap",
        alignItems: "center",
        mb: 1.25,
      }}
    >
      <TextField
        size="small"
        placeholder="Search violations…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: "1 1 200px",
          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
        }}
      />

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={sortOrder}
          label="Sort By"
          onChange={(e) => onSort(e.target.value)}
          sx={{ borderRadius: "10px" }}
        >
          <MenuItem value="newest">Newest First</MenuItem>
          <MenuItem value="oldest">Oldest First</MenuItem>
        </Select>
      </FormControl>
    </Box>

    {statuses.length > 0 && (
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip
          label="All"
          size="small"
          onClick={() => onStatusFilter(null)}
          sx={{
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: statusFilter === null ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
            color: statusFilter === null ? "#2f63ad" : "#5f6b7a",
            border: statusFilter === null ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
          }}
        />
        {statuses.map((s) => (
          <Chip
            key={s}
            label={s}
            size="small"
            onClick={() => onStatusFilter(s === statusFilter ? null : s)}
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: statusFilter === s ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
              color: statusFilter === s ? "#2f63ad" : "#5f6b7a",
              border: statusFilter === s ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
            }}
          />
        ))}
      </Box>
    )}
  </Box>
);

// ─── Violation card ──────────────────────────────────────────────────────────

const ViolationCard = ({ violation, onView }) => {
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/violation-files/${violation.id}`)
      .then((res) => {
        if (cancelled) return;
        const img = (res.data || []).find((f) => f.file_type?.startsWith("image/"));
        if (img) {
          setThumbUrl(
            `${API_BASE_URL}/violationsimages/${violation.id}/${encodeURIComponent(img.file_name)}`
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [violation.id]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...nestedCardSx,
        p: 0,
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      {/* Image thumbnail (left) */}
      {thumbUrl && (
        <Box
          component="img"
          src={thumbUrl}
          alt="Violation"
          sx={{
            width: 110,
            minWidth: 110,
            objectFit: "cover",
            borderRadius: "14px 0 0 14px",
            flexShrink: 0,
            display: "block",
          }}
        />
      )}

      {/* Card content (right) */}
      <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
              Violation #{violation.id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 600 }}>
              {violation.type || "N/A"}
            </Typography>
          </Box>
          <Chip size="small" label={violation.status || "Unknown"} sx={getStatusChipSx(violation.status)} />
        </Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5, flexGrow: 1 }}>
          {violation.description || "No description available."}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={onView}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            View Details
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ViolationSummary = ({ violations = [] }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileDetail, setMobileDetail] = useState({ open: false, id: null });
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [statusFilter, setStatusFilter] = useState(null);

  const resolved = useMemo(
    () =>
      violations.filter((v) =>
        ["resolved", "closed", "violation cured", "withdrawn / canceled"].includes(
          String(v.status || "").toLowerCase()
        )
      ).length,
    [violations]
  );

  const openCount = violations.length - resolved;

  // Unique statuses from data for filter chips
  const uniqueStatuses = useMemo(
    () => [...new Set(violations.map((v) => v.status).filter(Boolean))].sort(),
    [violations]
  );

  // Apply search + sort + status filter
  const filteredViolations = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = violations;

    if (q) {
      result = result.filter(
        (v) =>
          String(v.id).includes(q) ||
          String(v.type || "").toLowerCase().includes(q) ||
          String(v.description || "").toLowerCase().includes(q) ||
          String(v.status || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter) {
      result = result.filter((v) => v.status === statusFilter);
    }

    return [...result].sort((a, b) => {
      const da = new Date(a.created_at || 0);
      const db = new Date(b.created_at || 0);
      return sortOrder === "newest" ? db - da : da - db;
    });
  }, [violations, search, sortOrder, statusFilter]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const handleSort = (val) => {
    setSortOrder(val);
    setPage(1);
  };

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setPage(1);
  };

  const paginatedViolations = filteredViolations.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const isFiltering = search.trim() !== "" || statusFilter !== null;
  const previewItems = isFiltering ? filteredViolations.slice(0, 4) : violations.slice(0, 2);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {/* Section Header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Violation Summary
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review current violations and recent account activity.
          </Typography>
        </Box>

        {/* Summary stat cards */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Total Violations</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{violations.length}</Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(217,119,6,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <GavelOutlinedIcon sx={{ color: "#d97706" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Open Violations</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{openCount}</Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <WarningAmberOutlinedIcon sx={{ color: "#d97706" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Resolved</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{resolved}</Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(34,197,94,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#15803d" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Preview section */}
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
            Recent Violations
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.75 }}>
            A quick preview of the latest violations for this account.
          </Typography>

          <FilterToolbar
            search={search}
            onSearch={handleSearch}
            sortOrder={sortOrder}
            onSort={handleSort}
            statuses={uniqueStatuses}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
          />
        </Box>

        {violations.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              No violations found for this account.
            </Typography>
          </Paper>
        ) : filteredViolations.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              No violations match your search or filter.
            </Typography>
          </Paper>
        ) : (
          <>
            <Grid container spacing={2.5} sx={{ mb: 2 }}>
              {previewItems.map((violation) => (
                <Grid item xs={12} md={6} key={violation.id}>
                  <ViolationCard
                    violation={violation}
                    onView={() => isMobile
                      ? setMobileDetail({ open: true, id: violation.id })
                      : navigate(`/home/memberspage/violation/${violation.id}`)
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {filteredViolations.length > previewItems.length && (
              <Box sx={{ pt: 3, pb: 1, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#2f63ad",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#1f4f94", boxShadow: "0 8px 20px rgba(47,99,173,0.22)" },
                  }}
                >
                  View All {filteredViolations.length !== violations.length
                    ? `${filteredViolations.length} Matching `
                    : ""}Violations
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Full List Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", boxShadow: "0 18px 60px rgba(0,0,0,0.16)" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          All Violations
          {filteredViolations.length !== violations.length && (
            <Typography component="span" variant="body2" sx={{ color: "#6b778c", ml: 1, fontWeight: 400 }}>
              ({filteredViolations.length} of {violations.length})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <FilterToolbar
            search={search}
            onSearch={handleSearch}
            sortOrder={sortOrder}
            onSort={handleSort}
            statuses={uniqueStatuses}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
          />

          {paginatedViolations.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#6b778c", textAlign: "center", py: 3 }}>
              No violations match your search or filter.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {paginatedViolations.map((violation) => (
                <Grid item xs={12} key={violation.id}>
                  <ViolationCard
                    violation={violation}
                    onView={() => {
                      handleClose();
                      isMobile
                        ? setMobileDetail({ open: true, id: violation.id })
                        : navigate(`/home/memberspage/violation/${violation.id}`);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 1, p: 2 }}>
          <Pagination
            count={Math.ceil(filteredViolations.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
          />
          <Button onClick={handleClose} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile detail dialog */}
      <Dialog
        open={mobileDetail.open}
        onClose={() => setMobileDetail({ open: false, id: null })}
        fullScreen
        PaperProps={{ sx: { m: 0, borderRadius: 0 } }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            backgroundColor: "rgba(255,255,255,0.95)",
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backdropFilter: "blur(4px)",
          }}
        >
          <IconButton onClick={() => setMobileDetail({ open: false, id: null })}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto" }}>
          {mobileDetail.id && (
            <MemberViolationDetails
              id={String(mobileDetail.id)}
              onClose={() => setMobileDetail({ open: false, id: null })}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ViolationSummary;
